import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Award, Download, Share2, Lock, CheckCircle2, Loader2, ShieldCheck } from "lucide-react";
import api, { API } from "@/lib/api";
import { fireBigConfetti } from "@/lib/confetti";

export default function McNextCertificatePage() {
  const [eligibility, setEligibility] = useState(null);
  const [certificate, setCertificate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/mc-next/certificate/eligibility");
      setEligibility(data);
      const certRes = await api.get("/mc-next/certificate/me");
      if (certRes.data) setCertificate(certRes.data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const generate = async () => {
    setGenerating(true);
    try {
      const { data } = await api.post("/mc-next/certificate/generate");
      setCertificate(data);
      toast.success("MC Next Certificate generated!");
      fireBigConfetti();
    } catch (e) {
      toast.error("Not eligible yet");
    } finally {
      setGenerating(false);
    }
  };

  const shareLinkedIn = () => {
    const url = `${API}/mc-next/certificate/${certificate.certificate_id}`;
    window.open(`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`, "_blank");
  };

  if (loading || !eligibility) {
    return (
      <div className="min-h-screen flex items-center justify-center" data-testid="mc-next-certificate-loading">
        <div className="w-10 h-10 border-2 border-fuchsia-400 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 py-8" data-testid="mc-next-certificate-page">
      <div className="text-center mb-8">
        <Award className="w-10 h-10 text-[#FBBF24] mx-auto mb-3" />
        <span className="text-xs font-bold uppercase tracking-[0.2em] text-fuchsia-400">Agentic Marketing Track</span>
        <h1 className="font-heading text-3xl font-semibold text-white mt-1">MC Next Certificate of Completion</h1>
      </div>

      {certificate ? (
        <div className="glass-panel rounded-2xl p-10 text-center relative overflow-hidden" data-testid="mc-next-certificate-issued-panel">
          <div className="absolute inset-0 bg-gradient-to-br from-fuchsia-500/10 to-violet-500/10" />
          <div className="relative">
            <p className="text-xs uppercase tracking-[0.2em] text-fuchsia-400">Cloudivo</p>
            <h2 className="font-heading text-2xl font-semibold text-white mt-3">MC Next 30-Day Challenge - Certificate of Completion</h2>
            <p className="text-slate-400 mt-4">This certifies successful completion with a score of</p>
            <p className="font-heading text-3xl font-bold text-fuchsia-400 mt-1">{certificate.score}%</p>
            <p className="text-xs text-slate-400 mt-4">Certificate ID: {certificate.certificate_id}</p>
            {certificate.integrity_hash && (
              <div className="inline-flex items-center gap-1.5 mt-3 bg-emerald-500/10 border border-emerald-500/30 rounded-full px-3 py-1" data-testid="mc-next-certificate-integrity-badge">
                <ShieldCheck className="w-3.5 h-3.5 text-emerald-400" />
                <span className="text-[11px] font-semibold text-emerald-400">Cryptographically Signed & Verified</span>
              </div>
            )}
            <div className="flex items-center justify-center gap-3 mt-8">
              <a
                href={`${API}/mc-next/certificate/${certificate.certificate_id}/pdf`} data-testid="mc-next-download-certificate-pdf-btn"
                className="flex items-center gap-2 bg-fuchsia-500 hover:bg-fuchsia-400 text-white text-sm font-semibold px-5 py-2.5 rounded-lg transition-colors"
              >
                <Download className="w-4 h-4" /> Download PDF
              </a>
              <button onClick={shareLinkedIn} data-testid="mc-next-share-linkedin-btn" className="flex items-center gap-2 bg-white/10 hover:bg-white/15 text-white text-sm font-semibold px-5 py-2.5 rounded-lg transition-colors">
                <Share2 className="w-4 h-4" /> Share on LinkedIn
              </button>
            </div>
          </div>
        </div>
      ) : (
        <div className="glass-panel rounded-2xl p-8" data-testid="mc-next-certificate-eligibility-panel">
          <div className="space-y-4 mb-6">
            <RequirementRow met={eligibility.completed_count >= eligibility.total_days} label={`Complete all ${eligibility.total_days} MC Next days`} detail={`${eligibility.completed_count}/${eligibility.total_days}`} />
            <RequirementRow met={eligibility.avg_score >= eligibility.passing_score} label={`Average score of ${eligibility.passing_score}%+`} detail={`${eligibility.avg_score}%`} />
            <RequirementRow met={eligibility.capstone_completed} label="Final capstone day (Agentic Campaign for a Multi-Brand Retailer) completed" />
          </div>
          <button
            onClick={generate} disabled={!eligibility.eligible || generating} data-testid="mc-next-generate-certificate-btn"
            className="w-full flex items-center justify-center gap-2 bg-fuchsia-500 hover:bg-fuchsia-400 disabled:opacity-40 text-white font-semibold py-3 rounded-lg transition-colors"
          >
            {generating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Award className="w-4 h-4" />}
            {eligibility.eligible ? "Generate My MC Next Certificate" : "Complete requirements to unlock"}
          </button>
        </div>
      )}
    </div>
  );
}

function RequirementRow({ met, label, detail }) {
  return (
    <div className="flex items-center justify-between" data-testid="mc-next-certificate-requirement-row">
      <div className="flex items-center gap-2">
        {met ? <CheckCircle2 className="w-4 h-4 text-emerald-400" /> : <Lock className="w-4 h-4 text-slate-400" />}
        <span className={`text-sm ${met ? "text-white" : "text-slate-400"}`}>{label}</span>
      </div>
      {detail && <span className="text-xs text-slate-400">{detail}</span>}
    </div>
  );
}
